/**
 * Inclusão de ligações em lote — o `IncluirLigacoes` do Sistema antigo.
 *
 * **As rotas existiam e ninguém as chamava.** `GET {id}/ligacoes/candidatos` e
 * `POST {id}/ligacoes` estão na API desde o bloco de edição fina, e cobrem cinco
 * ações do legado (`IncluirLigacoes`, `IncluirLigacoesTudo`,
 * `IncluirLigacoesAterramento` e os dois `Finalizar`). Faltava a tela.
 *
 * **Dois eixos, e eles não são a mesma coisa.**
 * - `completo` é o "tudo": inclui os componentes de circuito de aterramento na
 *   lista oferecida.
 * - `aterramento` manda o motor **não** simplificar o aterramento ao montar os
 *   pinos — é o que a tela de aterramento do legado faz.
 *
 * O `completo` precisa ir nos dois lados: o `GET` monta a lista com ele e o
 * `POST` remonta a mesma lista antes de filtrar o que foi marcado. Mandar
 * diferente é pedir para o servidor filtrar uma lista que a tela nunca mostrou.
 *
 * **O que se marca é a linha: o pinout e o pinout de onde ele sai.** O corpo
 * leva esses `pares`, e o servidor grava só eles. O legado cruzava duas listas,
 * pinouts × componentes, e isso não separa os aterramentos — o ponto de massa é
 * o mesmo pinout em todas as linhas que aterram nele, e marcar um trazia todos.
 * As duas listas continuam indo, tiradas dos pares, para quem lê o formato antigo.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { api, noProjeto, obter } from "./cliente"

/** Um pino oferecido dentro de um componente candidato. */
export type PinoCandidato = {
  pinoutId: number
  /** O pinout do outro lado — o terminal a que este se liga. */
  ligacaoPinoutId: number
  /** O pino do componente candidato — onde a ligação chega. */
  pino: string
  componenteTipo: number
  pinoutTipo: number
  circuitoTipo: number
  /** O pino de onde a ligação sai: o do componente principal (ou o do componente, na inclusão dele). */
  pinoPrincipal: string
  /** O nome do circuito ("1BRPT1.E") e o apelido dele. */
  circuito: string
  circuitoApelido: string
  conector: string
  cor: string
}

export type ComponenteCandidato = {
  componenteId: number
  componenteCodigo: string
  componenteDescricao: string
  /** O nome pelo qual a equipe conhece o componente ("Motor da Borboleta Eletrônica"). */
  componenteApelido: string
  posicao: string
  ordem: number
  ligacoes: PinoCandidato[]
}

export type ResultadoDaInclusao = {
  diagramaId: number
  componentesCandidatos: number
  componentesInseridos: number
  ligacoesInseridas: number
}

/**
 * Os candidatos que a tela oferece. **Não grava nada** — mas monta o diagrama
 * inteiro no servidor para descobri-los, então não é uma consulta barata.
 */
export function usarCandidatosDeLigacao(
  diagramaId: number, completo: boolean, aterramento: boolean,
) {
  return useQuery({
    queryKey: ["ligacoes-candidatos", diagramaId, completo, aterramento] as const,
    enabled: diagramaId > 0,
    staleTime: 30_000,
    queryFn: () => obter<ComponenteCandidato[]>(
      `api/diagramas/${diagramaId}/ligacoes/candidatos`, { completo, aterramento }),
  })
}

/**
 * Uma linha marcada: o pinout que entra e o pinout de onde ele sai.
 *
 * O pinout sozinho não identifica a linha. O ponto de massa é **o mesmo pinout**
 * para todo pino que aterra nele: marcar um aterramento marcava todos os do
 * circuito, e eles entravam juntos. A API recebe os pares e grava só essas linhas.
 */
export type ParMarcado = Pick<PinoCandidato, "pinoutId" | "ligacaoPinoutId">

/** A chave da linha, para guardar a marcação num `Set`. */
export function chaveDoPar(p: ParMarcado): string {
  return `${p.pinoutId}-${p.ligacaoPinoutId}`
}

/** A linha marcada com o componente candidato dela — o que a gravação manda. */
export type LinhaMarcada = ParMarcado & { componenteId: number }

/**
 * O corpo da inclusão: os pares e, para quem ainda lê o formato do legado, as
 * duas listas que eles formam.
 */
export function corpoDosPares(pares: LinhaMarcada[]) {
  return {
    pares: pares.map(({ pinoutId, ligacaoPinoutId }) => ({ pinoutId, ligacaoPinoutId })),
    pinoutIds: [...new Set(pares.map((p) => p.pinoutId))],
    componenteIds: [...new Set(pares.map((p) => p.componenteId))],
  }
}

/** Grava as linhas marcadas. **Escreve no grafo do diagrama.** */
export function usarIncluirLigacoes(projetoId: number, diagramaId: number) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (
      { pares, completo, aterramento }: {
        pares: LinhaMarcada[]
        completo: boolean
        aterramento: boolean
      },
    ) => {
      const r = await api.post<ResultadoDaInclusao>(
        `api/diagramas/${diagramaId}/ligacoes?aterramento=${aterramento}`,
        { ...corpoDosPares(pares), completo },
        noProjeto(projetoId))
      return r.data
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["ligacoes-candidatos", diagramaId] })
      qc.invalidateQueries({ queryKey: ["diagrama-componentes", diagramaId] })
      qc.invalidateQueries({ queryKey: ["diagrama-desenho-estado", diagramaId] })
      qc.invalidateQueries({ queryKey: ["diagrama-verificacao", diagramaId] })
      qc.invalidateQueries({ queryKey: ["diagramas", projetoId] })
    },
  })
}
